import type { ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { formatPercent } from '@/shared/analytics/MetricFormatting';
import { difficultyColor, dimValue } from './InteractionRecordDrawer';

interface EvidenceBreakdownPanelProps {
    records: InteractionRecord[];
}

interface BreakdownSlice {
    name: string;
    value: number;
}

/**
 * The prompt-level side rail's difficulty donut: how the slice's turns split
 * across difficulty tiers, computed client-side from the loaded evidence
 * records. Renders nothing when the records carry no difficulty dimension.
 */
export default function EvidenceBreakdownPanel({ records }: EvidenceBreakdownPanelProps): ReactElement | null {
    const counts = new Map<string, number>();
    for (const record of records) {
        const value = dimValue(record, 'difficulty');
        if (value === '—') continue;
        counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    const total = Array.from(counts.values()).reduce((sum, count) => sum + count, 0);
    if (total === 0) return null;

    const slices: BreakdownSlice[] = Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({ name, value }));
    const top = slices[0];

    const fillFor = (name: string): string => `var(--mantine-color-${difficultyColor(name)}-6)`;

    return (
        <SurfaceCard $padding="md">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.06em' }}>
                Difficulty
            </Text>
            <Box style={{ position: 'relative', height: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={slices} dataKey="value" nameKey="name" innerRadius={46} outerRadius={64} paddingAngle={2} stroke="none" isAnimationActive={false}>
                            {slices.map((slice) => (
                                <Cell key={slice.name} fill={fillFor(slice.name)} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                {top !== undefined && (
                    <Box style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                        <Text fw={700} ff="monospace" style={{ fontSize: '16px', lineHeight: 1 }}>
                            {formatPercent(top.value / total)}
                        </Text>
                        <Text size="xs" c="dimmed" mt={2}>
                            {top.name}
                        </Text>
                    </Box>
                )}
            </Box>
            <Stack gap={4} mt="sm">
                {slices.map((slice) => (
                    <Group key={slice.name} gap={8} wrap="nowrap" justify="space-between">
                        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: fillFor(slice.name), flexShrink: 0 }} />
                            <Text size="xs" truncate>
                                {slice.name}
                            </Text>
                        </Group>
                        <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
                            {slice.value} · {formatPercent(slice.value / total)}
                        </Text>
                    </Group>
                ))}
            </Stack>
        </SurfaceCard>
    );
}
